<template>
  <header
    class="site-header"
    :class="{
      'site-header--fixed': isStickyHeader || isMobileMenuActive,
      'site-header--personal': isPersonal,
      'site-header--minimal': isMinimal,
      'site-header--mobile-menu-open': isMobileMenuActive,
    }"
  >
    <div
      v-if="isMobileMenuActive"
      class="site-header__mobile-backdrop"
      aria-hidden="true"
      @click="toggleMobileMenu"
    />
    <div class="container site-header__container">
      <div class="header-left">
        <HeaderLogo @click="sendGoal"/>
      </div>
      <nav
        v-if="!isMinimal"
        :id="menuNavId"
        ref="menuRef"
        :class="{'header-middle--show' : isMobileMenuActive}"
        aria-label="Основная навигация"
        class="header-middle"
      >
        <HeaderMenu/>
      </nav>
      <div v-if="!isMinimal" class="header-right">
        <HeaderCity variant="toolbar" />
        <HeaderThemeSwitch/>
        <HeaderProfile :isPersonal="isPersonal"/>
        <div class="header-burger-anchor">
          <button
              ref="burgerRef"
              :aria-controls="menuNavId"
              :aria-expanded="isMobileMenuActive"
              :aria-label="isMobileMenuActive ? 'Скрыть меню' : 'Показать меню'"
              :class="{'active' : isMobileMenuActive}"
              class="header-burger"
              type="button"
              @click="toggleMobileMenu"
          ><span></span></button>
        </div>
      </div>
    </div>
  </header>
  <button
      v-if="isMobileMenuActive && !isMinimal"
      aria-label="Закрыть меню"
      class="header-backdrop"
      type="button"
      @click="toggleMobileMenu"
  ></button>
</template>

<script setup lang="ts">
import {useModal} from 'vue-final-modal';
import {GAevent} from "~/utils/metrica-goals";
import {useUniqueId} from '~/composables/useUniqueId';
import {useCityStore} from '~/stores/city';

const CitySidebar = defineAsyncComponent(() => import('~/components/Modal/CitySidebar.vue'));

useServerHead({
  script: [{
    key: 'header-auth-session-hint',
    innerHTML: `try{const s=JSON.parse(localStorage.getItem('authSession'));if(typeof s?.exp==='number'&&s.exp*1000>Date.now())document.documentElement.dataset.authSession='true'}catch{}`,
  }],
});

const props = defineProps<{
  isPersonal?: boolean;
  variant?: 'default' | 'minimal';
}>();

const isMinimal = computed(() => props.variant === 'minimal');

const cityStore = useCityStore();

const { open: openCitySidebar, close: closeCitySidebar } = useModal({
  component: CitySidebar,
  attrs: {
    onClose() {
      closeCitySidebar();
      cityStore.closeSidebar();
    },
  },
});

watch(() => cityStore.sidebarOpen, (isOpen) => {
  if (isOpen) {
    openCitySidebar();
  } else {
    closeCitySidebar();
  }
});
//const { isPWA } = usePWA();


const menuNavId = useUniqueId('header-nav')

const isMobileViewport = useHeaderMobileLayout()
const isStickyHeader = ref(false);
const isOpenMobileMenu = ref(false);
const isMobileMenuActive = computed(() =>
  isOpenMobileMenu.value && isMobileViewport.value,
)
const menuRef = ref<HTMLElement | null>(null);
const burgerRef = ref<HTMLButtonElement | null>(null);

provide('headerMobileMenuOpen', isOpenMobileMenu)

const route = useRoute();

const fixedHeaderHandler = () => {
  if (document.body.classList.contains('clinics-detail-stickyheader-show')) return;
  isStickyHeader.value = window.scrollY > 16;
};

let _menuClosedByNavigation = false;

const toggleMobileMenu = () => {
  isOpenMobileMenu.value = !isOpenMobileMenu.value;
}

const onMenuDocumentKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && isOpenMobileMenu.value) {
    event.preventDefault();
    isOpenMobileMenu.value = false;
  }
};

watch(isMobileViewport, (mobile) => {
  if (!mobile && isOpenMobileMenu.value) {
    isOpenMobileMenu.value = false
  }
})

watch(isOpenMobileMenu, async (isOpen) => {
  if (import.meta.client) {
    document.body.classList.toggle('no-scroll', isOpen);
  }

  if (!import.meta.client) {
    _menuClosedByNavigation = false;
    return;
  }

  if (isOpen) {
    document.addEventListener('keydown', onMenuDocumentKeydown);
  } else {
    document.removeEventListener('keydown', onMenuDocumentKeydown);
  }

  if (isOpen && menuRef.value) {
    await nextTick();
    const focusable = menuRef.value.querySelector<HTMLElement>(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    focusable?.focus();
  } else if (!isOpen && !_menuClosedByNavigation) {
    await nextTick();
    burgerRef.value?.focus();
  }
  _menuClosedByNavigation = false;
});

onMounted(() => {
  fixedHeaderHandler();
  requestAnimationFrame(fixedHeaderHandler);
  window.addEventListener('scroll', fixedHeaderHandler);
});
onUnmounted(() => {
  window.removeEventListener('scroll', fixedHeaderHandler);
  if (import.meta.client) {
    document.removeEventListener('keydown', onMenuDocumentKeydown);
    document.body.classList.remove('no-scroll');
  }
});

watch(route, () => {
  _menuClosedByNavigation = true;
  isOpenMobileMenu.value = false;
}, {immediate: true});

const sendGoal = () => {
  GAevent({
    category: 'Header',
    action: 'click',
    label: 'Logo',
  });
}
</script>

<style lang="scss">
.site-header {
  position: sticky;
  top: calc(16px + var(--promo-banner-offset, 0px));
  z-index: 50;

  &__container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 24px;
    overflow-x: auto;
    overflow-y: visible;
    transition: background-color 0.3s ease, box-shadow 0.3s ease,
      border-radius 0.3s ease, padding 0.3s ease;

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }

    & > * {
      flex: 1;
    }

    @include media(header-nav, 'min-width') {
      overflow-x: visible;
      gap: 8px;

      & > * {
        flex: initial;
      }

      > .header-left {
        flex: 0 0 auto;
      }

      > .header-middle {
        flex: 1 1 auto;
        min-width: 0;
        display: flex;
        justify-content: center;
      }

      > .header-right {
        flex: 0 0 auto;
        flex-shrink: 0;
      }
    }

    @include media(header-nav) {
      flex-wrap: wrap;
      gap: 0;

      & > * {
        flex: 0 0 auto;
      }
    }
  }

  &--fixed {
    background-color: transparent !important;
    padding-left: $container-padding-x;
    padding-right: $container-padding-x;
  }

  &--fixed &__container {
    padding: 24px 40px;
    background-color: $color-white;
    box-shadow: 0 4px 56px -16px #6A00731A;
    border-radius: 64px;
    z-index: 10;
    max-width: 1240px;

    @include media(header-nav) {
      padding: 20px 24px;
      border-radius: 32px;
    }
  }

  .logo-icon {
    width: 95px;
    height: unset;
    margin-bottom: 0;

    @include media(header-nav) {
      width: 83px;
      max-width: 100%;
    }
  }

  &--personal {
    .header-right {
      width: fit-content;
    }
  }

  &--minimal {
    .site-header__container {
      justify-content: flex-start;
    }

    .header-left {
      flex: 0 0 auto;
    }
  }
}

.header-backdrop {
  display: none;

  @include media(header-nav) {
    display: block;
    position: fixed;
    inset: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 49;
    border: none;
    padding: 0;
    margin: 0;
    cursor: pointer;
    appearance: none;
  }
}

.header-left,
.header-right {
  display: flex;
  align-items: center;
  gap: 8px;

  @include media(md) {
    max-width: none;
  }
}

.header-left {
  gap: 5px;

  @include media(header-nav, 'min-width') {
    width: auto;
  }
}

.header-right {
  justify-content: flex-end;
}


@include media(header-nav, 'min-width') {
  .header-middle .personal-menu {
    display: none;
  }
}

@include media(header-nav) {
  .site-header--mobile-menu-open .site-header__mobile-backdrop {
    display: block;
    position: fixed;
    inset: 0;
    z-index: 9;
    background: rgba(33, 39, 42, 0.45);
  }

  .site-header--mobile-menu-open {
    z-index: 50;
  }

  .site-header--mobile-menu-open .site-header__container {
    position: fixed;
    z-index: 10;
    left: $container-padding-x;
    right: $container-padding-x;
    top: calc(16px + var(--promo-banner-offset, 0px));
    max-width: 1240px;
    margin-left: auto;
    margin-right: auto;
    max-height: calc(
      100dvh - 16px - var(--promo-banner-offset, 0px) - env(safe-area-inset-bottom, 0px)
    );
    overflow-y: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
  }

  .header-left {
    max-width: none;
    width: auto;
    flex: 0 0 auto;
  }

  .header-right {
    max-width: none;
    width: auto;
    min-width: 0;
    flex: 1 1 auto;
  }

  .header-middle {
    order: 13;
    width: 100%;
    max-width: 100%;
    margin-top: 32px;
    display: none;
    margin-bottom: 0;

    &--show {
      display: block;
      max-height: calc(100dvh - 160px - var(--promo-banner-offset, 0px));
      overflow-y: auto;
      overscroll-behavior: contain;
      scrollbar-width: none;
      -ms-overflow-style: none;

      &::-webkit-scrollbar {
        display: none;
      }
    }
  }

  .header-left {
    .subscription-button {
      width: 40px;

      svg {
        height: auto;
      }
    }
  }
}

.header-burger-anchor {
  position: relative;
  display: flex;
  align-items: center;
}

.header-burger {
  line-height: 0;
  position: relative;
  height: 28px;
  width: 28px;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 0;
  appearance: none;
  border: none;
  background-color: transparent;

  span {
    display: block;
    position: relative;
    width: 22px;
    height: 2px;
    border-radius: 10px;
    background: $color-purple;
    transition: .3s;
    pointer-events: none;
    margin-inline: auto;

    &::before,
    &::after {
      content: '';
      position: absolute;
      left: 0;
      top: -7px;
      width: 100%;
      height: 2px;
      background: $color-purple;
      transition: .3s;
    }

    &::after {
      bottom: -7px;
      top: unset;
    }
  }

  &.active {
    span {
      background-color: $color-white;
      transform: rotate(180deg);

      &::before,
      &::after {
        transform-origin: center;
        top: 0;
      }

      &::before {
        transform: rotate(45deg);
      }

      &::after {
        transform: rotate(-45deg);
      }
    }
  }

  @include media(header-nav, min-width) {
    display: none;
  }
}
</style>
